import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useRef } from 'react';

import { WorkOrderData } from '../../../../api/workOrders/types';

interface UseClusteredMarkersArgs {
  items: WorkOrderData[];
  colorOf: (item: WorkOrderData) => string;
  onSelect: (item: WorkOrderData) => void;
  onBoundsIds?: (ids: number[]) => void;
}

const buildIcon = (color: string): google.maps.Symbol => ({
  path: google.maps.SymbolPath.CIRCLE,
  fillColor: color,
  fillOpacity: 1,
  strokeColor: '#FFFFFF',
  strokeWeight: 2,
  scale: 8,
});

const buildIdSignature = (items: WorkOrderData[]): string =>
  items
    .map((it) => it.id)
    .sort()
    .join(',');

export function useClusteredMarkers({
  items,
  colorOf,
  onSelect,
  onBoundsIds,
}: UseClusteredMarkersArgs): void {
  const map = useMap();
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<Map<number, google.maps.Marker>>(new Map());
  const lastIdSignatureRef = useRef<string>('');
  const fittedOnceRef = useRef(false);

  useEffect(() => {
    if (!map) return undefined;
    if (typeof google === 'undefined' || !google.maps) return undefined;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current.clear();
    if (clustererRef.current) clustererRef.current.clearMarkers();

    const validItems = items.filter((it) => {
      const lat = Number(it.latitude);
      const lng = Number(it.longitude);
      return !Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0;
    });

    const markers = validItems.map((it) => {
      const marker = new google.maps.Marker({
        position: {
          lat: Number(it.latitude),
          lng: Number(it.longitude),
        },
        icon: buildIcon(colorOf(it)),
        title: `OS ${it.reference_number}`,
      });
      marker.addListener('click', () => onSelect(it));
      markersRef.current.set(it.id, marker);
      return marker;
    });

    if (!clustererRef.current) {
      clustererRef.current = new MarkerClusterer({ map, markers });
    } else {
      clustererRef.current.addMarkers(markers);
    }

    // Only fit bounds when the actual set of OS changes (or on the first
    // successful render). This prevents the disruptive zoom-in / zoom-out
    // that happens when only `colorOf` changes (toggling SLA ↔ Status).
    const signature = buildIdSignature(validItems);
    if (
      markers.length > 0 &&
      (!fittedOnceRef.current || lastIdSignatureRef.current !== signature)
    ) {
      const bounds = new google.maps.LatLngBounds();
      markers.forEach((m) => {
        const pos = m.getPosition();
        if (pos) bounds.extend(pos);
      });
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 80);
        fittedOnceRef.current = true;
        lastIdSignatureRef.current = signature;
      }
    }

    let boundsListener: google.maps.MapsEventListener | null = null;
    if (onBoundsIds) {
      const emit = (): void => {
        const bounds = map.getBounds();
        if (!bounds) return;
        const visible: number[] = [];
        markersRef.current.forEach((m, id) => {
          const pos = m.getPosition();
          if (pos && bounds.contains(pos)) visible.push(id);
        });
        onBoundsIds(visible);
      };
      boundsListener = map.addListener('idle', emit);
      emit();
    }

    return () => {
      if (boundsListener) boundsListener.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, items, colorOf]);

  useEffect(
    () => () => {
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current.clear();
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
    },
    []
  );
}
